/**
 * Money: an integer count of a currency's minor units.
 *
 * Two rules are enforced by the shape of the API rather than by discipline:
 *
 *  1. Arithmetic between amounts is exact. Add and subtract cannot round.
 *  2. Anything that *could* round returns a {@link Decimal}, and the way back
 *     is {@link toMoney}, which demands a rounding mode. Every rounding in the
 *     system is therefore a call site you can grep for.
 *
 * This is the only money type in the monorepo (docs/25 §1.2). The DTO shape
 * in `@shoppy/shared` is {@link MoneyJson}.
 */
import { raise } from "../errors/index.js";
import { getCurrency } from "./currency.js";
import { decimal, decimalToString, multiplyDecimal, rescale, subtractDecimal, } from "./decimal.js";
export function money(units, currency) {
    getCurrency(currency);
    return { currency, units };
}
export function zeroMoney(currency) {
    return money(0n, currency);
}
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        raise("CURRENCY_MISMATCH", { expected: a.currency, actual: b.currency });
    }
}
export function addMoney(a, b) {
    assertSameCurrency(a, b);
    return { currency: a.currency, units: a.units + b.units };
}
export function subtractMoney(a, b) {
    assertSameCurrency(a, b);
    return { currency: a.currency, units: a.units - b.units };
}
export function sumMoney(amounts, currency) {
    let total = 0n;
    for (const amount of amounts) {
        if (amount.currency !== currency) {
            raise("CURRENCY_MISMATCH", { expected: currency, actual: amount.currency });
        }
        total += amount.units;
    }
    return money(total, currency);
}
export function negateMoney(value) {
    return { currency: value.currency, units: -value.units };
}
export function absMoney(value) {
    return value.units < 0n ? negateMoney(value) : value;
}
export function isZeroMoney(value) {
    return value.units === 0n;
}
export function isNegativeMoney(value) {
    return value.units < 0n;
}
export function compareMoney(a, b) {
    assertSameCurrency(a, b);
    return a.units === b.units ? 0 : a.units < b.units ? -1 : 1;
}
export function equalsMoney(a, b) {
    return a.currency === b.currency && a.units === b.units;
}
/** Exact view of an amount as a decimal at the currency's scale. */
export function toDecimal(value) {
    return decimal(value.units, getCurrency(value.currency).minorScale);
}
/** The rounding boundary: the only way to make money from arithmetic. Names its mode. */
export function toMoney(value, currency, mode) {
    const { minorScale } = getCurrency(currency);
    return money(rescale(value, minorScale, mode).units, currency);
}
/** As {@link toMoney}, and what it discarded — so `amount + remainder` is the input. */
export function toMoneyWithRemainder(value, currency, mode) {
    const amount = toMoney(value, currency, mode);
    return { amount, remainder: subtractDecimal(value, toDecimal(amount)) };
}
/**
 * Scale an amount by a rate. Returns a full-precision Decimal on purpose: a
 * chain of multiplications rounds once at the end, not at every step.
 */
export function multiplyMoney(value, factor) {
    return multiplyDecimal(toDecimal(value), factor);
}
export function moneyToString(value) {
    return `${decimalToString(toDecimal(value))} ${value.currency}`;
}
export function moneyToJson(value) {
    return { currency: value.currency, units: value.units.toString() };
}
export function moneyFromJson(value) {
    if (!/^-?\d+$/.test(value.units)) {
        raise("VALUE_INVALID", { kind: "money", value: value.units, reason: "units is not an integer string" });
    }
    return money(BigInt(value.units), currencyCodeOf(value.currency));
}
function currencyCodeOf(code) {
    return getCurrency(code).code;
}
//# sourceMappingURL=money.js.map